import { useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  CurecallModalContainer,
  CurecallSearchableSelect,
  CurecallButton,
  ECurecallButtonAppearance,
  CurecallMultiSelect,
  CurecallSearchableSelectItem,
  ECurecallNotificationAppearance,
  TOptionWithNumberValue,
} from '@honestica/curecall-ui'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useTranslation } from 'react-i18next'
import { TLabel, TNavigationLocales } from 'src/types/locales'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ProjectService } from 'src/services/project.service'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { TFollowup } from 'src/types/followup'
import { TMedEventUI } from 'src/types/med-event'
import { ChatbotService } from 'src/services/chatbot.service'
import { getErrorMessage } from 'src/utils/http.util'
import { useNotification } from 'src/providers/notification.provider'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useLocale } from 'src/hooks/use-locale'

type TFollowupWithIntlName = TFollowup & {
  intlName?: TLabel
}

type TDuplicateChatbotFormValues = {
  projectId: number
  followupId: number
  medEventIds: number[]
}

type Props = {
  isOpen: boolean
  onClose: () => void
  chatbot: {
    id: number
    name: string
  }
}

export function DuplicateChatbotModal({ isOpen, onClose, chatbot }: Readonly<Props>) {
  const { t } = useTranslation()
  const { project, followups: currentProjectFollowups, medEvents } = useCurrentProject()
  const { followupId } = useParams()
  const { locale } = useLocale()
  const currentFollowupId = Number(followupId)
  const { notify } = useNotification()
  const navigate = useLocalizedNavigate()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)

  const methods = useForm<TDuplicateChatbotFormValues>({
    defaultValues: {
      projectId: project.id,
      followupId: currentFollowupId,
      medEventIds: [],
    },
  })

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = methods
  const watchedProjectId = watch('projectId')
  const watchedFollowupId = watch('followupId')

  const { data: userProjects = [] } = useQuery({
    queryKey: ['project-list'],
    queryFn: () => ProjectService.getProjectsList(),
  })
  const { data: otherProjectData } = useQuery({
    queryKey: ['project', watchedProjectId],
    queryFn: () => ProjectService.getById(watchedProjectId),
    enabled: watchedProjectId !== project.id,
  })

  const { data: chatbotDetails } = useQuery({
    queryKey: ['chatbot', project.id, currentFollowupId, chatbot.id],
    queryFn: () => ChatbotService.get(project.id, currentFollowupId, chatbot.id),
    enabled: isOpen,
  })

  // Get followups of the selected project
  const followupsOfTargetProject: TFollowupWithIntlName[] = useMemo(() => {
    if (watchedProjectId === project.id) {
      return currentProjectFollowups as TFollowupWithIntlName[]
    }
    return (otherProjectData?.Followups || []) as TFollowupWithIntlName[]
  }, [watchedProjectId, project.id, currentProjectFollowups, otherProjectData])

  const PROJECT_OPTIONS: CurecallSearchableSelectItem[] = useMemo(
    () => userProjects.map((project) => ({ value: project.id, label: project.name })),
    [userProjects],
  )
  const FOLLOWUP_OPTIONS: CurecallSearchableSelectItem[] = useMemo(
    () =>
      followupsOfTargetProject.map((followup) => ({
        value: followup.id,
        label: getTranslatedLabel(locale, followup.intlName) || followup.name || `Followup ${followup.id}`,
      })),
    [followupsOfTargetProject, locale],
  )
  const MED_EVENT_OPTIONS: TOptionWithNumberValue[] = useMemo(() => {
    if (!watchedFollowupId) return []
    const selectedFollowup = followupsOfTargetProject.find((f) => f.id === watchedFollowupId)
    if (!selectedFollowup?.configUI?.medEventsUI) return []

    const medEventsSource = watchedProjectId === project.id ? medEvents : otherProjectData?.MedEvents || []

    return selectedFollowup.configUI.medEventsUI.map((medEventUI: TMedEventUI) => ({
      value: medEventUI.MedEvent.id,
      label: getTranslatedLabel(
        locale as TNavigationLocales,
        medEventsSource.find((me) => me.id === medEventUI.MedEvent.id)?.intlName,
      ),
    }))
  }, [
    watchedFollowupId,
    followupsOfTargetProject,
    locale,
    watchedProjectId,
    project.id,
    medEvents,
    otherProjectData,
  ])

  const isDifferentFollowup = watchedFollowupId !== currentFollowupId
  const selectedProjectOption = PROJECT_OPTIONS.find((p) => p.value === watchedProjectId)
  const selectedFollowupOption = FOLLOWUP_OPTIONS.find((f) => f.value === watchedFollowupId)
  const selectedMedEventsOptions = MED_EVENT_OPTIONS.filter((m) => watch('medEventIds').includes(m.value))

  // Update followup selection when project changes
  useEffect(() => {
    if (watchedProjectId === project.id) {
      // When returning to current project, select current followup
      setValue('followupId', currentFollowupId)
      setValue('medEventIds', [])
    } else {
      // When switching to another project, select first followup
      const firstFollowup = followupsOfTargetProject[0]
      if (firstFollowup) {
        setValue('followupId', firstFollowup.id)
      }
      // Reset medEvents
      setValue('medEventIds', [])
    }
  }, [watchedProjectId, project.id, followupsOfTargetProject, currentFollowupId, setValue])

  useEffect(() => {
    if (chatbotDetails && watchedFollowupId === currentFollowupId) {
      setValue('medEventIds', chatbotDetails.medEventIds)
    }
  }, [chatbotDetails, watchedFollowupId, currentFollowupId, setValue])

  const handleChangeProject = (item: CurecallSearchableSelectItem) => {
    clearErrors('projectId')
    setValue('projectId', item.value)
  }

  const handleChangeFollowup = (item: CurecallSearchableSelectItem) => {
    clearErrors('followupId')
    setValue('followupId', item.value)
    setValue('medEventIds', [])
  }

  const handleChangeMedEvents = (items: TOptionWithNumberValue[]) => {
    clearErrors('medEventIds')
    setValue(
      'medEventIds',
      items.map((item) => item.value),
    )
  }

  const handleConfirm = async (formData: TDuplicateChatbotFormValues) => {
    setIsLoading(true)
    try {
      const response = await ChatbotService.clone(project.id, currentFollowupId, chatbot.id, {
        targetProjectId: formData.projectId,
        targetFollowupId: formData.followupId,
        medeventListIds: formData.medEventIds,
      })

      notify({
        title: t('pages.configuration.followups.chatbot.duplicate.success'),
        message: t('pages.configuration.followups.chatbot.duplicate.success-message'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })

      // Invalidate caches
      queryClient.invalidateQueries({
        queryKey: ['followup-self-configuration', response.targetProjectId, response.targetFollowupId],
      })

      // Redirect to cloned chatbot edit page (step 1)
      navigate(
        `/${response.targetProjectId}/configuration/followups/${response.targetFollowupId}/chatbot/${response.id}/update-general-form`,
      )

      onClose()
    } catch (err) {
      notify({
        title: t('pages.configuration.followups.chatbot.duplicate.error'),
        message: getErrorMessage(err),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <CurecallModalContainer
      isOpen={isOpen}
      toggle={onClose}
      closeModalAriaLabel={t('common.actions.close')}
      dialogClassName="c-max-w-[600px]"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleConfirm)}>
          <div className="c-p-6">
            <h3 className="c-mb-6">
              {t('pages.configuration.followups.chatbot.duplicate.modal-title', { name: chatbot.name })}
            </h3>

            <div>
              {userProjects.length > 1 && (
                <Controller
                  control={control}
                  name="projectId"
                  render={() => (
                    <CurecallSearchableSelect
                      label={t('pages.configuration.followups.chatbot.duplicate.modal-project-label')}
                      defaultItemSelected={selectedProjectOption}
                      onSelect={handleChangeProject}
                      items={PROJECT_OPTIONS}
                      placeholder={t('pages.configuration.followups.chatbot.duplicate.modal-project-label')}
                      errorMessage={errors.projectId && t('common.form.required')}
                      wrapperClassName="c-pb-6"
                    />
                  )}
                />
              )}

              <Controller
                control={control}
                name="followupId"
                rules={{ required: true }}
                render={() => (
                  <CurecallSearchableSelect
                    label={t('pages.configuration.followups.chatbot.duplicate.modal-followup-label')}
                    defaultItemSelected={selectedFollowupOption}
                    onSelect={handleChangeFollowup}
                    items={FOLLOWUP_OPTIONS}
                    placeholder={t('pages.configuration.followups.chatbot.duplicate.modal-followup-label')}
                    isDisabled={followupsOfTargetProject.length === 0}
                    optionsClassName="c-max-h-[176px]"
                    wrapperClassName="c-pb-6"
                    errorMessage={errors.followupId && t('common.form.required')}
                  />
                )}
              />
              {followupsOfTargetProject.length === 0 && (
                <p className="c-text-sm c-text-red-600 c-mt-1 c-mb-6">
                  {t('pages.configuration.followups.chatbot.duplicate.modal-no-followup')}
                </p>
              )}

              {MED_EVENT_OPTIONS.length > 0 && (
                <Controller
                  control={control}
                  name="medEventIds"
                  rules={{ required: true }}
                  render={() => (
                    <CurecallMultiSelect
                      label={t('pages.configuration.followups.chatbot.general-form.appointment-list')}
                      isRequired={true}
                      items={MED_EVENT_OPTIONS}
                      selectedItems={selectedMedEventsOptions}
                      onSelect={handleChangeMedEvents}
                      selectedElementsLabel={`${selectedMedEventsOptions.length} ${t('pages.configuration.followups.chatbot.general-form.lists-selected')}`}
                      placeholder={t(
                        'pages.configuration.followups.chatbot.general-form.select-appointment-list',
                      )}
                      tooltipContent={t(
                        'pages.configuration.followups.chatbot.general-form.appointment-list-tooltip',
                      )}
                      tooltipPosition="right"
                      wrapperClassName="c-w-full c-relative"
                      errorMessage={errors.medEventIds && t('common.form.required')}
                    />
                  )}
                />
              )}

              <div className="c-flex c-justify-center c-gap-8 c-mt-32">
                <CurecallButton
                  type="button"
                  onClick={onClose}
                  appearance={ECurecallButtonAppearance.CANCEL}
                  disabled={isLoading}
                >
                  {t('pages.configuration.followups.chatbot.duplicate.modal-cancel')}
                </CurecallButton>
                <CurecallButton
                  type="submit"
                  isLoading={isLoading}
                  disabled={
                    followupsOfTargetProject.length === 0 ||
                    (isDifferentFollowup &&
                      watchedProjectId === project.id &&
                      selectedMedEventsOptions.length === 0)
                  }
                >
                  {t('pages.configuration.followups.chatbot.duplicate.modal-duplicate')}
                </CurecallButton>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </CurecallModalContainer>
  )
}
